// Application finder.
//
// The model this is built on, which is the opposite of how it looks:
// **the application does not filter the catalogue.** A 5 HP 1800 RPM TEFC
// three-phase motor genuinely suits pumps and conveyors and fans and
// compressors, and the store's own data says so — 97% of labelled products
// carry "Pumping Applications". Filtering on that returns everything.
//
// So the application chooses the *questions and the sizing*, and the answers
// filter on what actually discriminates: HP, RPM, phase, voltage, enclosure and
// frame. Those narrow 508 motors to a handful.
//
// Pure functions are exposed on window.MotorFinder so scripts/test-finder.mjs
// can exercise the matching without driving the UI.
(function () {
  'use strict';

  // Index column order, pinned to scripts/build-finder-index.mjs FIELDS.
  var HANDLE = 0,
    TITLE = 1,
    CLASS = 2,
    HP = 3,
    RPM = 4,
    PHASE = 5,
    INPUT_PHASE = 6,
    VOLTS = 7,
    ENCLOSURE = 8,
    FRAME = 9,
    PRICE = 10,
    FLAGS = 11,
    IMAGE = 12;

  // NEMA standard ratings. A sizing estimate always rounds *up* to one of these
  // — an undersized motor runs hot and dies early, which is the failure this
  // whole business exists to prevent.
  var NEMA_HP = [
    0.25, 0.33, 0.5, 0.75, 1, 1.5, 2, 3, 5, 7.5, 10, 15, 20, 25, 30, 40, 50, 60, 75, 100,
    125, 150, 200, 250, 300, 350, 400, 450, 500,
  ];

  // The largest single-phase-input drive in the catalogue. Above this a
  // single-phase shop needs a rotary phase converter, or a three-phase drive
  // derated for single-phase supply.
  //
  // A test asserts this matches the catalogue, so stocking a larger one fails
  // the build rather than quietly sending customers to a converter they do not
  // need. That is how it moved from 3 to 5: the Invertek ODE-3-320153-1042.
  var MAX_SINGLE_PHASE_VFD_HP = 5;

  function roundUpToNema(hp) {
    for (var i = 0; i < NEMA_HP.length; i += 1) {
      if (NEMA_HP[i] >= hp - 0.0001) return NEMA_HP[i];
    }
    return NEMA_HP[NEMA_HP.length - 1];
  }

  // --- sizing ---------------------------------------------------------------
  //
  // Only two applications get a formula, and both are the standard textbook
  // ones. The rest get published typical ranges instead of a computed number,
  // because a conveyor's horsepower depends on incline, friction and duty cycle
  // in ways four form fields cannot capture, and inventing a number that looks
  // authoritative is worse than admitting the question needs a person.

  // Water horsepower over pump efficiency, then over motor efficiency headroom.
  function estimatePumpHp(gpm, headFeet, specificGravity, efficiency) {
    var sg = specificGravity || 1;
    var eff = efficiency || 0.65;
    if (!(gpm > 0) || !(headFeet > 0)) return null;
    return (gpm * headFeet * sg) / (3960 * eff);
  }

  // CFM against static pressure in inches of water gauge.
  function estimateFanHp(cfm, staticPressure, efficiency) {
    var eff = efficiency || 0.6;
    if (!(cfm > 0) || !(staticPressure > 0)) return null;
    return (cfm * staticPressure) / (6356 * eff);
  }

  var APPLICATIONS = {
    pump: {
      label: 'Pump',
      hint: 'Centrifugal, turbine, irrigation or booster',
      defaultRpm: 3600,
      sizing: 'pump',
      // A vertical turbine pump needs a hollow-shaft motor. This is one of the
      // few genuine application-to-product constraints in the catalogue.
      subtypes: [
        { id: 'centrifugal', label: 'Centrifugal or booster', rpm: 3600 },
        { id: 'turbine', label: 'Vertical turbine or deep well', rpm: 1800, require: ['vhs'] },
        { id: 'submersible', label: 'Submersible', rpm: 3600 },
      ],
    },
    saw: {
      label: 'Saw or shop machine',
      hint: 'Table saw, planer, jointer, lathe, mill',
      defaultRpm: 3600,
      sizing: 'guide',
      guide: [
        ['10 in table saw', '3 HP'],
        ['12 in table saw', '5 HP'],
        ['14 in or larger', '7.5 – 10 HP'],
        ['Planer or jointer', '3 – 7.5 HP'],
        ['Lathe or mill', '2 – 5 HP'],
      ],
    },
    conveyor: {
      label: 'Conveyor',
      hint: 'Belt, roller, screw or auger',
      defaultRpm: 1800,
      sizing: 'guide',
      guide: [
        ['Light packages, level run', '0.5 – 2 HP'],
        ['General duty, level', '2 – 5 HP'],
        ['Inclined or loaded', '5 – 15 HP'],
        ['Screw or auger', '3 – 20 HP'],
      ],
      prefer: ['cface'],
    },
    compressor: {
      label: 'Compressor',
      hint: 'Reciprocating or rotary screw',
      defaultRpm: 1800,
      sizing: 'guide',
      guide: [
        ['Up to 20 CFM at 100 psi', '5 HP'],
        ['20 – 35 CFM', '7.5 – 10 HP'],
        ['35 – 60 CFM', '15 HP'],
        ['60 CFM and above', '20 HP and up'],
      ],
    },
    fan: {
      label: 'Fan or blower',
      hint: 'Exhaust, supply, process or cooling',
      defaultRpm: 1800,
      sizing: 'fan',
    },
    dust: {
      label: 'Dust collection',
      hint: 'Shop or process dust collector',
      defaultRpm: 3600,
      sizing: 'guide',
      guide: [
        ['Single machine, short duct', '1.5 – 3 HP'],
        ['Two or three machines', '3 – 5 HP'],
        ['Shop-wide ducted system', '5 – 10 HP'],
        ['Process or cyclone', '10 HP and up'],
      ],
    },
    hvac: {
      label: 'HVAC',
      hint: 'Air handler, cooling tower, pump',
      defaultRpm: 1800,
      sizing: 'fan',
    },
    known: {
      label: 'I already know the specs',
      hint: 'Replacing a motor, or working from a drawing',
      sizing: 'direct',
    },
  };

  // The six enclosure classes, named the way the trade names them and chosen by
  // the job rather than the code. Nobody knows they want TEFC; everybody knows
  // they have a rock crusher.
  //
  // `stock` is what the catalogue actually holds, so the interface can be honest
  // when the right answer is something we would have to source.
  var ENVIRONMENTS = [
    {
      id: 'odp',
      label: 'Clean and dry, indoors',
      hint: 'Commercial HVAC supply fans, pumps in clean utility rooms, office air compressors',
      enclosure: 'ODP',
      nema: 'NEMA 1',
    },
    {
      id: 'weather',
      label: 'Outdoors, exposed to real weather',
      hint: 'Deep-well vertical turbine pumps, pipeline booster pumps',
      enclosure: 'WPI',
      nema: 'NEMA 3R',
    },
    {
      id: 'tefc',
      label: 'Dusty, wet, or knocked about',
      hint: 'Rock crushers, conveyors in dusty plants, pulp pumps',
      enclosure: 'TEFC',
      nema: 'NEMA 12',
    },
    {
      id: 'tenv',
      label: 'Fine dust or fumes a fan would make worse',
      hint: 'Textile machinery, paint spray booths, garage door openers',
      enclosure: 'TENV',
      nema: 'NEMA 12',
      // No cooling fan at all: in a textile mill a fan clogs with fibre, and in
      // a spray booth it would move atomised paint around. Sealed and cooled by
      // its own surface instead, which is why they run at lower ratings.
      note:
        'Non-ventilated motors have no cooling fan, so they shed heat through ' +
        'their frame alone. If this is a spray booth, the drive belongs outside ' +
        'the booth — the area inside is classified.',
    },
    {
      id: 'teao',
      label: 'Mounted in the airstream it drives',
      hint: 'Cooling tower fans, exhaust blowers, crop drying',
      enclosure: 'TEAO',
      nema: 'NEMA 3R',
      // Air-over motors are cooled by the air the fan moves, so they must never
      // be used anywhere else — stop the fan and the motor cooks. We hold none,
      // and a TEFC is a valid substitute because it carries its own fan.
      note:
        'Air-over motors rely on the fan they drive for cooling, so they only ' +
        'work in the airstream. We do not stock them — a TEFC does the same job ' +
        'and cools itself, which is what we would supply.',
      substitute: 'TEFC',
    },
    {
      id: 'washdown',
      label: 'Hosed down and sanitised',
      hint: 'Meat and poultry conveyors, dairy bottling, pharmaceutical mixing',
      require: ['wash'],
      nema: 'NEMA 4X',
    },
    {
      id: 'xp',
      label: 'Explosive atmosphere',
      hint: 'Fuel dispensing, grain elevator drives, refinery mixers',
      require: ['xp'],
      nema: null,
    },
  ];

  // How the motor needs to be controlled. This is a separate question from shop
  // power: single-phase service forces a drive for compatibility, but plenty of
  // three-phase customers want one for speed control or soft starting, and
  // without asking they were being told "you need nothing extra" and shown none
  // of the 169 drives in the catalogue.
  var CONTROL_OPTIONS = [
    {
      id: 'fixed',
      label: 'Full speed, on and off',
      hint: 'The usual case — a starter is all it needs',
      drive: false,
    },
    {
      id: 'variable',
      label: 'Variable speed',
      hint: 'Match flow or airflow to demand. The big energy saver on pumps and fans.',
      drive: true,
    },
    {
      id: 'soft',
      label: 'Soft start',
      hint: 'Ease it up to speed to cut inrush current and mechanical shock',
      drive: true,
    },
    { id: 'unsure', label: "I'm not sure", hint: 'We will work it out with you', drive: false },
  ];

  var POWER_OPTIONS = [
    {
      id: '1p',
      label: 'Single-phase (standard shop or residential service)',
      phase: 1,
      volts: 230,
    },
    { id: '3p230', label: 'Three-phase 230V', phase: 3, volts: 230 },
    { id: '3p460', label: 'Three-phase 460V', phase: 3, volts: 460 },
    { id: 'unsure', label: "I'm not sure", phase: null, volts: null },
  ];

  // --- matching -------------------------------------------------------------

  function decode(row) {
    return {
      handle: row[HANDLE],
      title: row[TITLE],
      kind: row[CLASS],
      isDrive: row[CLASS] === 'd',
      isStarter: row[CLASS] === 's',
      hp: row[HP],
      rpm: row[RPM],
      phase: row[PHASE],
      inputPhase: row[INPUT_PHASE],
      volts: row[VOLTS] || [],
      enclosure: row[ENCLOSURE],
      frame: row[FRAME],
      price: row[PRICE],
      flags: row[FLAGS] || [],
      image: row[IMAGE] || null,
    };
  }

  // Some drives put out single phase — the Optidrive E3 models ending in -01 are
  // built for permanent split capacitor and shaded pole motors. They cannot turn
  // a three-phase motor, so they must never appear in an answer to "how do I run
  // my three-phase machine". For a drive, row[PHASE] holds its output phase.
  function drivesThreePhaseMotor(product) {
    return product.phase !== 1;
  }

  function hasFlags(product, required) {
    if (!required || !required.length) return true;
    for (var i = 0; i < required.length; i += 1) {
      if (product.flags.indexOf(required[i]) === -1) return false;
    }
    return true;
  }

  // Ranked rather than filtered: a hard filter on six attributes returns zero
  // results the moment one of them is slightly off, and "no results" on a
  // 508-motor catalogue is always wrong. HP and required flags are the only
  // hard constraints; everything else scores.
  function matchProducts(rows, criteria) {
    var wanted = criteria || {};
    var results = [];

    var kind = wanted.kind || (wanted.drivesOnly ? 'd' : 'm');

    for (var i = 0; i < rows.length; i += 1) {
      var product = decode(rows[i]);
      if (product.kind !== kind) continue;
      if (!hasFlags(product, wanted.require)) continue;

      if (wanted.hp) {
        // Never offer anything undersized. The ceiling is deliberately different
        // by kind: nobody wants a 20 HP motor when they asked for 10, and an
        // oversized starter carries an overload relay whose trip range is too
        // wide to protect the motor. A reactor is just an inductor — running one
        // rated higher is electrically harmless, and with sparse coverage a
        // larger one beats offering nothing on a run that needs protection.
        if (product.hp < wanted.hp) continue;
        var ceiling = wanted.hpCeiling || 1.4;
        if (product.hp > roundUpToNema(wanted.hp * ceiling)) continue;
      }

      // Voltage is a hard constraint, not a preference. A 460V starter on 230V
      // service does not work, and scoring it merely sorted it lower instead of
      // removing it. Products whose voltage we could not read are kept — absence
      // of data is not evidence of incompatibility — they just score lower.
      if (wanted.volts && product.volts.length && product.volts.indexOf(wanted.volts) === -1) {
        continue;
      }

      var score = 0;
      if (wanted.hp && product.hp === wanted.hp) score += 50;
      if (wanted.rpm && product.rpm === wanted.rpm) score += 25;
      if (wanted.phase && product.phase === wanted.phase) score += 20;
      if (wanted.volts && product.volts.indexOf(wanted.volts) !== -1) score += 20;
      if (wanted.enclosure && product.enclosure === wanted.enclosure) score += 15;

      if (wanted.prefer) {
        for (var f = 0; f < wanted.prefer.length; f += 1) {
          if (product.flags.indexOf(wanted.prefer[f]) !== -1) score += 8;
        }
      }
      // Inverter-duty matters whenever a drive is in the picture.
      if (wanted.needsDrive && product.flags.indexOf('inv') !== -1) score += 12;

      results.push({ product: product, score: score });
    }

    results.sort(function (a, b) {
      if (b.score !== a.score) return b.score - a.score;
      if (a.product.hp !== b.product.hp) return a.product.hp - b.product.hp;
      return (a.product.price || Infinity) - (b.product.price || Infinity);
    });

    return results;
  }

  // What it takes to actually run the chosen motor on the customer's service.
  // This is the part competitors don't answer, and for single-phase it's the
  // whole reason the kit program exists.
  function powerPath(shopPower, hp, rows) {
    if (shopPower.phase === 3) {
      return {
        mode: 'direct',
        headline: 'This runs on your existing service.',
        detail:
          'Three-phase service drives a three-phase motor directly. A starter or ' +
          'drive is optional, for control rather than compatibility.',
        drives: [],
      };
    }

    if (shopPower.phase !== 1) return null;

    if (hp && hp <= MAX_SINGLE_PHASE_VFD_HP) {
      var drives = matchProducts(rows, {
        drivesOnly: true,
        hp: hp,
      }).filter(function (result) {
        return result.product.inputPhase === 1 && drivesThreePhaseMotor(result.product);
      });

      return {
        mode: 'vfd',
        headline: 'A single-phase input drive runs this motor on your shop power.',
        detail:
          'The drive takes single-phase in and puts three-phase out, so a ' +
          'three-phase motor runs on standard shop service. Pair it with an ' +
          'inverter-duty motor.',
        drives: drives.slice(0, 3),
      };
    }

    return {
      mode: 'converter',
      headline: 'Above ' + MAX_SINGLE_PHASE_VFD_HP + ' HP, this needs a phase converter.',
      detail:
        'Single-phase input drives stop at ' +
        MAX_SINGLE_PHASE_VFD_HP +
        ' HP. Past that, a rotary phase converter and magnetic starter is the ' +
        'path — we build these as matched kits so the sizing is done for you.',
      drives: [],
    };
  }

  // Everything the customer needs beyond the motor itself — the drive, and the
  // parts we don't stock but they still can't run without.
  //
  // Being straight about what we don't sell matters more than it looks. A motor
  // that arrives without the protection to run it is the failure this catalogue
  // exists to prevent, and "call us and we'll source it" is a better outcome
  // than silence.
  function systemAdvice(shopPower, control, hp, rows) {
    var power = powerPath(shopPower, hp, rows);
    var wantsDrive = Boolean(control && control.drive);
    var powerForcesDrive = Boolean(power && power.mode === 'vfd');
    var converterCase = Boolean(power && power.mode === 'converter');
    var needsDrive = (wantsDrive || powerForcesDrive) && !converterCase;

    var drives = [];
    if (needsDrive) {
      var singlePhaseInput = shopPower.phase === 1;
      drives = matchProducts(rows, {
        drivesOnly: true,
        hp: hp,
        volts: shopPower.volts,
      })
        .filter(function (result) {
          if (!drivesThreePhaseMotor(result.product)) return false;
          // On single-phase service only a single-phase-input drive is any use.
          return singlePhaseInput ? result.product.inputPhase === 1 : true;
        })
        .slice(0, 3);
    }

    var reason = null;
    if (wantsDrive && powerForcesDrive) reason = 'both';
    else if (wantsDrive) reason = 'control';
    else if (powerForcesDrive) reason = 'power';

    // A motor running straight off the line needs a starter: it switches the
    // motor and, more importantly, carries the overload relay. This is the
    // protection whose absence is the most common cause of early motor failure,
    // so the finder names an actual product rather than leaving it to chance.
    var starters = [];
    if (!needsDrive) {
      starters = matchProducts(rows, {
        kind: 's',
        hp: hp,
        volts: shopPower.volts,
      }).slice(0, 2);
    }

    // Reactors only matter when a drive is involved — they exist to deal with
    // what a drive does to the supply and to the motor. Both sides are offered
    // with what each one is for, rather than asking the customer another
    // question about cable length they may not have measured yet.
    var reactors = { line: [], load: [] };
    if (needsDrive) {
      reactors.line = matchProducts(rows, {
        kind: 'r',
        hp: hp,
        hpCeiling: 3,
        volts: shopPower.volts,
        require: ['z3'],
      }).slice(0, 1);
      reactors.load = matchProducts(rows, {
        kind: 'r',
        hp: hp,
        hpCeiling: 3,
        volts: shopPower.volts,
        require: ['z5'],
      }).slice(0, 1);
    }

    var sourced = [];
    if (converterCase) {
      // Two routes exist above the single-phase drive ceiling, and the second is
      // not something to auto-recommend: Invertek rate the E3 for single-phase
      // supply at 50% derate, so a drive of roughly double the motor's rating
      // works — with exceptions (Size 4 outdoor chassis use film capacitors and
      // cannot). Sizing that correctly is a conversation, not a filter.
      sourced.push(
        'A rotary phase converter and magnetic starter. We do not list these ' +
          'online — call and we will size the set with the motor.'
      );
      sourced.push(
        'There is a second route: a three-phase drive at roughly double this ' +
          'rating, derated for single-phase supply. It is cheaper than a ' +
          'converter and gives you speed control, but not every model can do ' +
          'it and the sizing has to be right. Call and we will work it out.'
      );
    } else if (!needsDrive && !starters.length) {
      // Only fall back to the phone when nothing in stock fits — a starter is
      // rated for a specific HP at a specific voltage, and we do not carry every
      // combination yet.
      sourced.push(
        'A magnetic starter and overload relay to protect the motor. Nothing in ' +
          'stock matches this rating — call and we will source it. Skipping this ' +
          'protection is the most common cause of early motor failure.'
      );
    }

    return {
      power: power,
      needsDrive: needsDrive,
      reason: reason,
      drives: drives,
      starters: starters,
      reactors: reactors,
      // A standard motor on a drive overheats at low speed: the drive's carrier
      // frequency stresses insulation the motor was never wound for. If a drive
      // is in the picture the motor has to be inverter duty.
      requireInverterDuty: needsDrive,
      sourced: sourced,
    };
  }

  // What a buyer actually compares on. Order matters: this is the order the
  // comparison reads in, and it runs from "will it fit the job" down to
  // "what does it cost".
  var COMPARE_ROWS = [
    { key: 'hp', label: 'Horsepower', format: function (p) { return p.hp ? p.hp + ' HP' : null; } },
    { key: 'rpm', label: 'Speed', format: function (p) { return p.rpm ? p.rpm + ' RPM' : null; } },
    {
      key: 'volts',
      label: 'Voltage',
      format: function (p) { return p.volts.length ? p.volts.join('/') + 'V' : null; },
    },
    { key: 'enclosure', label: 'Enclosure', format: function (p) { return p.enclosure; } },
    { key: 'frame', label: 'Frame', format: function (p) { return p.frame; } },
    { key: 'inv', label: 'Inverter duty', flag: 'inv' },
    { key: 'pe', label: 'Premium efficiency', flag: 'pe' },
    { key: 'sev', label: 'Severe duty', flag: 'sev' },
    { key: 'ci', label: 'Cast iron', flag: 'ci' },
    { key: 'cface', label: 'C-face mount', flag: 'cface' },
    { key: 'vhs', label: 'Vertical hollow shaft', flag: 'vhs' },
    { key: 'wash', label: 'Washdown duty', flag: 'wash' },
    {
      key: 'price',
      label: 'Price',
      format: function (p) {
        return p.price ? '$' + Number(p.price).toLocaleString('en-US', { maximumFractionDigits: 0 }) : null;
      },
    },
  ];

  // Builds the comparison, and works out what actually differs. That last part
  // is the point: nobody wants to scan thirteen rows to discover two motors are
  // identical apart from speed.
  function compare(products) {
    var rows = [];
    var differing = [];

    COMPARE_ROWS.forEach(function (spec) {
      var values = products.map(function (product) {
        if (spec.flag) return product.flags.indexOf(spec.flag) !== -1 ? 'Yes' : 'No';
        return spec.format(product);
      });

      // A row nobody has is noise, not a similarity worth reporting.
      var anyPresent = values.some(function (value) {
        return value && value !== 'No';
      });
      if (!anyPresent) return;

      var first = values[0];
      var same = values.every(function (value) {
        return value === first;
      });
      rows.push({ key: spec.key, label: spec.label, values: values, same: same });
      if (!same) differing.push(spec.label.toLowerCase());
    });

    return { rows: rows, differing: differing };
  }

  // --- the enclosure ---------------------------------------------------------
  //
  // An IP20 drive is a bare chassis, not a finished product — it cannot be hung
  // on a wall. Two thirds of the drives in this catalogue are IP20, so for most
  // customers the box is a missing part rather than an upsell.
  //
  // This specifies the box. It does not size it: that needs the drive's physical
  // dimensions and clearances, which the catalogue does not carry. Saying which
  // NEMA type and how much heat has to leave is the part that gets designed
  // wrong, and it is the part we can do honestly.

  // Modern IGBT drives dissipate roughly 3% of rated output as heat. A reactor
  // adds about another 1%.
  var DRIVE_LOSS = 0.03;
  var REACTOR_LOSS = 0.01;
  var WATTS_PER_HP = 746;

  // The motor's environment decides the drive's box too, but not to the same
  // rating: a NEMA number is not a motor enclosure code.
  var NEMA_BY_ENVIRONMENT = {
    odp: {
      type: 'NEMA 1',
      why: 'Indoor, general purpose. Guards against falling dirt but is not dust-tight.',
      ventable: true,
    },
    weather: {
      type: 'NEMA 3R',
      why: 'Outdoor, rated for rain and sleet, and ventilated so heat can still escape.',
      ventable: true,
    },
    tefc: {
      type: 'NEMA 12',
      why: 'Indoor, dust-tight and drip-tight. The industrial default.',
      ventable: true,
    },
    tenv: {
      type: 'NEMA 12',
      why: 'Dust-tight, so fibre and airborne particles stay out of the electronics.',
      ventable: true,
    },
    teao: {
      type: 'NEMA 3R',
      why: 'Outdoor, rated for rain and sleet. Cooling tower and fan decks are wet places.',
      ventable: true,
    },
    washdown: {
      type: 'NEMA 4X',
      why: 'Hose-directed water and corrosion resistant, in stainless or polycarbonate.',
      ventable: false,
    },
  };

  function panelSpec(environment, hp, drive, reactor) {
    if (!drive) return null;

    // An IP66 or NEMA 4X drive is already a finished assembly.
    var rating = drive.enclosure || '';
    if (/IP6|4X/.test(rating)) return null;

    var environmentId = (environment && environment.id) || 'tefc';

    // A drive in a hazardous area is a specialist design — purged enclosures,
    // area classification, and a drive that is usually mounted outside the
    // classified space entirely. Not something to spec from four questions.
    if (environmentId === 'xp') {
      return {
        specialist: true,
        headline: 'A drive in a hazardous area needs a specialist design.',
        detail:
          'Classified areas call for a purged or pressurised enclosure, and the ' +
          'drive is usually mounted outside the classified space altogether. ' +
          'That is an engineered job — call us and we will work it through with you.',
      };
    }

    var nema = NEMA_BY_ENVIRONMENT[environmentId] || NEMA_BY_ENVIRONMENT.tefc;

    var watts = Math.round(hp * WATTS_PER_HP * DRIVE_LOSS);
    if (reactor) watts += Math.round(hp * WATTS_PER_HP * REACTOR_LOSS);

    // Airflow to hold a given rise: CFM = 3.16 x watts / degrees F.
    // 20 degrees F of rise is the usual allowance against a 104F drive limit.
    var allowedRiseF = 20;
    var cfm = Math.ceil((3.16 * watts) / allowedRiseF);

    var notes = [];
    if (!nema.ventable) {
      notes.push(
        'A ' + nema.type + ' box is sealed, so it cannot be vented without losing ' +
          'its rating. At ' + watts + ' W you need a sealed heat exchanger or a ' +
          'panel cooler — not a fan.'
      );
    } else if (watts > 100) {
      notes.push(
        'At ' + watts + ' W a sealed box will overheat. Fit a filtered fan and ' +
          'exhaust rated at least ' + cfm + ' CFM, and keep the filters clean — a ' +
          'blocked filter is the same as no fan.'
      );
    } else {
      notes.push(
        'At ' + watts + ' W natural convection is usually enough in a reasonably ' +
          'sized box, provided it is not in direct sun or against a hot wall.'
      );
    }

    if (environmentId === 'tefc') {
      notes.push(
        'Mounted outdoors rather than in a dusty building, step this up to a ' +
          'NEMA 3R for rain and sleet.'
      );
    }

    notes.push(
      'Leave the clearance the drive manual asks for above and below it, usually ' +
        '100 to 150 mm. Final box dimensions come off the drive data sheet — send ' +
        'us the model and we will confirm the size.'
    );

    return {
      specialist: false,
      nema: nema.type,
      why: nema.why,
      watts: watts,
      cfm: nema.ventable && watts > 100 ? cfm : null,
      sealed: !nema.ventable,
      notes: notes,
    };
  }

  // --- asking us to source it ------------------------------------------------
  //
  // The catalogue is what we hold, not what we can get — we are authorised for
  // dozens of manufacturers and source outside stock most days. So a specific
  // enough request that we happen not to stock is a sale, and making the
  // customer retype their own specification into a contact form is how it gets
  // lost. This turns the answers into a request that can be read out, texted, or
  // pasted, without them typing any of it twice.
  function specificationText(state, options) {
    var settings = options || {};
    var lines = [];

    if (state.spec) lines.push('Driving: ' + state.spec.label);
    if (state.subtype) lines.push('Type: ' + state.subtype.label);
    if (state.hp) {
      lines.push('Rating: ' + state.hp + ' HP' + (state.rpm ? ' at ' + state.rpm + ' RPM' : ''));
    }
    if (state.power) lines.push('Shop power: ' + state.power.label);
    if (state.environment) {
      var environment = state.environment.label;
      if (state.environment.enclosure) environment += ' (' + state.environment.enclosure + ')';
      else if (state.environment.id === 'washdown') environment += ' (washdown duty)';
      else if (state.environment.id === 'xp') environment += ' (explosion proof)';
      lines.push('Environment: ' + environment);
    }
    if (state.control) lines.push('Control: ' + state.control.label);

    var body =
      'Please quote a motor to this specification:\n\n' + lines.join('\n');

    if (settings.url) body += '\n\nSpecified here: ' + settings.url;
    return body;
  }

  // True when the catalogue could not answer properly and a person should.
  function shouldOfferSourcing(matches, environment) {
    if (!matches.length) return true;
    // A hard requirement we can barely serve — washdown, explosion proof — is
    // worth offering even when something scraped through.
    if (environment && environment.require && matches.length < 3) return true;
    return false;
  }

  window.MotorFinder = {
    specificationText: specificationText,
    shouldOfferSourcing: shouldOfferSourcing,
    COMPARE_ROWS: COMPARE_ROWS,
    compare: compare,
    panelSpec: panelSpec,
    APPLICATIONS: APPLICATIONS,
    ENVIRONMENTS: ENVIRONMENTS,
    CONTROL_OPTIONS: CONTROL_OPTIONS,
    POWER_OPTIONS: POWER_OPTIONS,
    systemAdvice: systemAdvice,
    NEMA_HP: NEMA_HP,
    MAX_SINGLE_PHASE_VFD_HP: MAX_SINGLE_PHASE_VFD_HP,
    roundUpToNema: roundUpToNema,
    estimatePumpHp: estimatePumpHp,
    estimateFanHp: estimateFanHp,
    matchProducts: matchProducts,
    powerPath: powerPath,
    decode: decode,
  };
})();
